import useGetBillingPricingQuery from '@/api/queries/useGetBillingPricingQuery';
import Button from '@/components/Button';
import SpinnerIcon from '@/components/icons/SpinnerIcon';
import PageHeader from '@/components/PageHeader';
import StatusActiveIndicator from '@/components/StatusActiveIndicator';
import { BillingPricing, BillingPricingProduct } from '@/models/Billing';
import Breadcrumbs from '@/modules/admin/components/Breadcrumbs';
import {
  AdminBillingFailedTransactionsPath,
  AdminBillingHomePath,
  AdminBillingInvoiceListPath,
} from '@/modules/admin/paths';
import { formatPrice } from '@/utils/currency/formatPrice';
import { getCountryName } from '@/utils/locale/getCountryName';

const ownerTypeLabels: Record<BillingPricing['ownerType'], string> = {
  store: 'Store',
  supplier: 'Supplier',
  retailer: 'Retailer',
};

const ownerTypeOrder: Record<BillingPricing['ownerType'], number> = {
  retailer: 0,
  store: 1,
  supplier: 2,
};

const formatProductLabel = (product: string) => {
  return product
    .split('_')
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase())
    .join(' ');
};

const renderAmount = (
  product: BillingPricingProduct,
  field: 'unitPrice' | 'vatAmount' | 'totalAmount',
) => {
  return formatPrice(product[field]) ?? 'Not available';
};

const renderVatAmount = (product: BillingPricingProduct) => {
  const formattedVatAmount = renderAmount(product, 'vatAmount');
  if (!Number.isFinite(product.vatRate)) {
    return formattedVatAmount;
  }

  const vatRatePercent = product.vatRate * 100;
  const formattedVatRate = Number.isInteger(vatRatePercent)
    ? vatRatePercent.toFixed(0)
    : vatRatePercent.toFixed(2);

  return `${formattedVatAmount} (${formattedVatRate}%)`;
};

const getActiveProductCount = (pricing: BillingPricing) => {
  return pricing.plans.reduce((planTotal, plan) => {
    return (
      planTotal +
      plan.options.reduce((optionTotal, option) => {
        return optionTotal + option.products.filter((product) => product.isActive).length;
      }, 0)
    );
  }, 0);
};

const BillingPrices = () => {
  const { pricing, isLoading, error } = useGetBillingPricingQuery();
  const sortedPricing = [...pricing].sort((left, right) => {
    const leftHasActiveProducts = getActiveProductCount(left) > 0;
    const rightHasActiveProducts = getActiveProductCount(right) > 0;

    if (leftHasActiveProducts !== rightHasActiveProducts) {
      return leftHasActiveProducts ? -1 : 1;
    }

    return ownerTypeOrder[left.ownerType] - ownerTypeOrder[right.ownerType];
  });

  return (
    <>
      <Breadcrumbs />

      <PageHeader title="Billing Prices">
        <Button to={AdminBillingHomePath} variant="secondary">
          Billing Home
        </Button>
        <Button to={AdminBillingInvoiceListPath} variant="secondary">
          View Invoices
        </Button>
        <Button to={AdminBillingFailedTransactionsPath} variant="secondary">
          Failed Transactions
        </Button>
      </PageHeader>

      {isLoading ? (
        <div className="flex justify-center py-20">
          <SpinnerIcon />
        </div>
      ) : error ? (
        <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">
          Failed to load billing prices.
        </div>
      ) : pricing.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-600">
          No billing prices are configured.
        </div>
      ) : (
        <div className="space-y-6">
          {sortedPricing.map((ownerPricing) => (
            <section
              key={ownerPricing.ownerType}
              className="rounded-2xl border border-slate-200 bg-white p-6"
            >
              <div className="mb-6 flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">
                    {ownerTypeLabels[ownerPricing.ownerType]}
                  </h2>
                  <p className="mt-1 text-sm text-slate-600">
                    Pricing configuration grouped by plan, country, and currency.
                  </p>
                </div>
                <div className="rounded-lg bg-slate-100 px-3 py-1.5 text-sm font-medium text-slate-700">
                  {ownerPricing.plans.length} plan{ownerPricing.plans.length === 1 ? '' : 's'}
                </div>
              </div>

              <div className="space-y-6">
                {ownerPricing.plans.map((plan) => (
                  <div key={`${ownerPricing.ownerType}-${plan.planCode}`} className="space-y-4">
                    <div className="text-sm font-medium uppercase tracking-wide text-slate-500">
                      {plan.planCode}
                    </div>

                    {plan.options.map((option) => (
                      <div
                        key={`${plan.planCode}-${option.countryCode}-${option.currencyCode}`}
                        className="overflow-hidden rounded-xl border border-slate-200"
                      >
                        <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3 text-sm">
                          <span className="font-medium text-slate-900">
                            {getCountryName(option.countryCode) ?? option.countryCode}
                          </span>
                          <span className="rounded-md bg-white px-2 py-1 text-slate-600">
                            {option.countryCode}
                          </span>
                          <span className="rounded-md bg-white px-2 py-1 text-slate-600">
                            {option.currencyCode}
                          </span>
                        </div>

                        <div className="overflow-x-auto">
                          <table className="min-w-full table-fixed divide-y divide-slate-200 text-sm">
                            <colgroup>
                              <col className="w-[28%]" />
                              <col className="w-[16%]" />
                              <col className="w-[18.5%]" />
                              <col className="w-[18.5%]" />
                              <col className="w-[19%]" />
                            </colgroup>
                            <thead className="bg-white">
                              <tr className="text-left text-slate-500">
                                <th className="px-4 py-3 font-medium">Product</th>
                                <th className="px-4 py-3 font-medium">Status</th>
                                <th className="px-4 py-3 text-right font-medium">Unit Price</th>
                                <th className="px-4 py-3 text-right font-medium">VAT</th>
                                <th className="px-4 py-3 text-right font-medium">Total</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200 bg-white">
                              {option.products.map((product) => (
                                <tr
                                  key={`${option.countryCode}-${option.currencyCode}-${product.product}`}
                                  className="align-top"
                                >
                                  <td className="px-4 py-3 font-medium text-slate-900">
                                    {formatProductLabel(product.product)}
                                  </td>
                                  <td className="px-4 py-3 text-slate-700">
                                    <StatusActiveIndicator isActive={product.isActive} />
                                  </td>
                                  <td className="px-4 py-3 text-right tabular-nums text-slate-700">
                                    {renderAmount(product, 'unitPrice')}
                                  </td>
                                  <td className="px-4 py-3 text-right tabular-nums text-slate-700">
                                    {renderVatAmount(product)}
                                  </td>
                                  <td className="px-4 py-3 text-right tabular-nums text-slate-900">
                                    {renderAmount(product, 'totalAmount')}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </>
  );
};

export default BillingPrices;
